import { useMutation } from '@apollo/client';
import type { InsertControlTestOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertControlTestOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertControlTestOversightResultInput } from './useInsertControlTestOversightResultTRPC';
import { useInsertControlTestOversightResultTRPC } from './useInsertControlTestOversightResultTRPC';

export const useInsertControlTestOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertControlTestOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'control_test_oversight_result');
        evictField(cache, 'control');
        evictField(cache, 'oversight_assessment');
      },
    }
  );

  const trpcMutation = useInsertControlTestOversightResultTRPC();

  const insertControlTestOversightResult = async (
    variables: InsertControlTestOversightResultInput
  ): Promise<InsertControlTestOversightResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertControlTestOversightResult(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert control test oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertControlTestOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertControlTestOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
